// Utils
import getConfig from '@config';
import { ListingsSearchType, SearchPageContentType } from './gql/generated';

export const routes = {
  home: '/',
  accounts: (slug: string) => `/accounts/${slug}`,
  listings: ({
    slug,
    checkInDate,
    checkOutDate,
    adultCount,
    childCount,
    infantCount,
    petCount,
  }: {
    slug: string;
    checkInDate?: string;
    checkOutDate?: string;
    adultCount?: number;
    childCount?: number;
    infantCount?: number;
    petCount?: number;
  }) => {
    let queryParams = new URLSearchParams();

    if (adultCount === undefined || adultCount === 0) {
      adultCount = 1;
    }

    if (checkInDate !== undefined) {
      queryParams.set('checkInDate', checkInDate);
    }

    if (checkOutDate !== undefined) {
      queryParams.set('checkOutDate', checkOutDate);
    }

    queryParams.set('adultCount', adultCount.toString());

    if (childCount !== undefined) {
      queryParams.set('childCount', childCount.toString());
    }

    if (infantCount !== undefined) {
      queryParams.set('infantCount', infantCount.toString());
    }

    if (petCount !== undefined) {
      queryParams.set('petCount', petCount.toString());
    }

    return concatParams(`/listings/${slug}`, queryParams);
  },
  comingSoon: '/a/coming-soon',
  searchShow: ({
    placeId,
    searchType,
    centerLat,
    centerLng,
    southWestLat,
    southWestLng,
    northEastLat,
    northEastLng,
    zoom,
    activeFeatures,
    checkInDate,
    checkOutDate,
    adultCount = 1,
    childCount = 0,
    infantCount = 0,
    petCount = 0,
    bedroomCount = 0,
    bathroomCount = 0,
    activePropertyTypes,
    activeUniquePropertyTypes,
  }: {
    placeId?: string;
    searchType?: string;
    centerLat?: string;
    centerLng?: string;
    southWestLat?: string;
    southWestLng?: string;
    northEastLat?: string;
    northEastLng?: string;
    zoom?: string;
    activeFeatures?: string;
    checkInDate?: string;
    checkOutDate?: string;
    adultCount?: number;
    childCount?: number;
    infantCount?: number;
    petCount?: number;
    bedroomCount?: number;
    bathroomCount?: number;
    activePropertyTypes?: string;
    activeUniquePropertyTypes?: string;
  }) => {
    let queryParams = new URLSearchParams();

    if (placeId) {
      queryParams.set('placeId', placeId);
    }

    if (searchType) {
      queryParams.set('searchType', searchType);
    }

    if (centerLat) {
      queryParams.set('centerLat', centerLat);
    }

    if (centerLng) {
      queryParams.set('centerLng', centerLng);
    }

    if (southWestLat) {
      queryParams.set('southWestLat', southWestLat);
    }

    if (southWestLng) {
      queryParams.set('southWestLng', southWestLng);
    }

    if (northEastLat) {
      queryParams.set('northEastLat', northEastLat);
    }

    if (northEastLng) {
      queryParams.set('northEastLng', northEastLng);
    }

    if (zoom) {
      queryParams.set('zoom', zoom);
    }

    if (activeFeatures) {
      queryParams.set('activeFeatures', activeFeatures);
    }

    if (checkInDate !== undefined) {
      queryParams.set('checkInDate', checkInDate);
    }

    if (checkInDate === undefined || null) {
      queryParams.set('checkInDate', '');
    }

    if (checkOutDate !== undefined) {
      queryParams.set('checkOutDate', checkOutDate);
    }

    if (checkOutDate === undefined || null) {
      queryParams.set('checkOutDate', '');
    }

    if (adultCount === undefined || adultCount === 0) {
      adultCount = 1;
    }

    queryParams.set('adultCount', adultCount.toString());
    queryParams.set('childCount', childCount.toString());
    queryParams.set('infantCount', infantCount.toString());
    queryParams.set('petCount', petCount.toString());

    queryParams.set('minNumberOfRooms', bedroomCount.toString());
    queryParams.set('minNumberOfBathrooms', bathroomCount.toString());

    if (activePropertyTypes) {
      queryParams.set('propertyTypes', activePropertyTypes);
    }

    if (activeUniquePropertyTypes) {
      queryParams.set('uniquePropertyTypes', activeUniquePropertyTypes);
    }

    return concatParams('/search', queryParams);
  },
  sitemaps: (searchType: SearchPageContentType) => {
    let slug: string;
    switch (searchType) {
      case SearchPageContentType.VacationRental:
        slug = 'vacation-rentals';
        break;
      default:
        slug = 'vacation-rentals';
    }

    return `/sitemaps/${slug}`;
  },
  seoVRSearchShow: (region: {
    countrySlug?: string;
    levelOneSlug?: string;
    citySlug?: string;
    slug?: string;
  }) => {
    return `/vacation-rentals/${regionToPath(region)}`;
  },
  accountListingsSearchShow: ({
    slug,
    checkInDate,
    checkOutDate,
    bedroomCount = 0,
    bathroomCount = 0,
    activePropertyTypes,
    activeUniquePropertyTypes,
  }: {
    slug: string;
    checkInDate?: string;
    checkOutDate?: string;
    bedroomCount?: number;
    bathroomCount?: number;
    activePropertyTypes?: string;
    activeUniquePropertyTypes?: string;
  }) => {
    let queryParams = new URLSearchParams();

    if (checkInDate) {
      queryParams.set('checkInDate', checkInDate);
    }

    if (checkOutDate) {
      queryParams.set('checkOutDate', checkOutDate);
    }

    if (bedroomCount) {
      queryParams.set('minNumberORooms', bedroomCount.toString());
    }
    if (bathroomCount) {
      queryParams.set('minNumberOfBathrooms', bathroomCount.toString());
    }

    if (activePropertyTypes) {
      queryParams.set('propertyTypes', activePropertyTypes);
    }

    if (activeUniquePropertyTypes) {
      queryParams.set('uniquePropertyTypes', activeUniquePropertyTypes);
    }

    queryParams.set('searchType', ListingsSearchType.Map);
    queryParams.set('northEastLat', '61.01572493360369');
    queryParams.set('northEastLng', '-66.884766');
    queryParams.set('southWestLat', '2.4601814276672704');
    queryParams.set('southWestLng', '-124.541016');
    queryParams.set('centerLat', '31.737953180635483');
    queryParams.set('centerLng', '-95.712891');
    queryParams.set('zoom', '3');

    return concatParams(`/search/account/${slug}`, queryParams);
  },
  help: 'https://finderco.my.site.com/help/s/',
  noFeesDisclaimer:
    'https://finderco.my.site.com/help/s/article/The-bnbfinder-Difference',
  giftCardRedemption:
    'https://finderco.my.site.com/help/s/savvy-gift-cards/travelers?tabset-7ec7a=3',
  hostawayHelp:
    'https://finderco.my.site.com/help/s/partner-integrations/hostaway',
  ownerRezHelp:
    'https://finderco.my.site.com/help/s/partner-integrations/ownerrez',
  escapiaHelp:
    'https://finderco.my.site.com/help/s/partner-integrations/escapia',
  streamlineHelp:
    'https://finderco.my.site.com/help/s/partner-integrations/streamline',
  barefootHelp:
    'https://finderco.my.site.com/help/s/partner-integrations/barefoot',
  trackHelp: 'https://finderco.my.site.com/help/s/partner-integrations/track',
  guestyHelp: 'https://finderco.my.site.com/help/s/partner-integrations/guesty',
  hostfullyHelp:
    'https://finderco.my.site.com/help/s/partner-integrations/hostfully',
  quickConnectHelp:
    'https://finderco.my.site.com/help/s/partner-integrations/quickconnect',
  lodgifyHelp:
    'https://finderco.my.site.com/help/s/partner-integrations/lodgify',
  ciirusHelp: 'https://finderco.my.site.com/help/s/partner-integrations/ciirus',
  nextPaxHelp:
    'https://finderco.my.site.com/help/s/partner-integrations/nextpax',
  integrationHelp: (integration: string) =>
    `https://finderco.my.site.com/help/s/partner-integrations/${integration}`,
  signup: (args?: { redirect?: string }) => {
    if (args && args.redirect) {
      return `/signup?redirect=${encodeURIComponent(args.redirect)}`;
    } else {
      return '/signup';
    }
  },
  login: '/login',
  loginWithRedirect: (args?: { redirect?: string; source?: string }) => {
    if (args && args.redirect && args.source) {
      return `/login?source=${encodeURIComponent(
        args.source
      )}&callbackUrl=${encodeURIComponent(args.redirect)}`;
    } else if (args && args.redirect) {
      return `/login?callbackUrl=${encodeURIComponent(args.redirect)}`;
    } else {
      return '/login';
    }
  },
  list: '/list',
  middleman: '/middleman',
  fiftyCashbackPromotionTerms: '/50-cashback-promotion-terms',
  about: '/about',
  blog: '/blog',
  careers: '/careers',
  integrations: '/integrations',
  partnerIntegrationShow: (id: string) => `/integrations/${id}`,
  accessibility: '/accessibility',
  search: '/search',
  mobileSearch: '/m/search',
  privacy: '/privacy',
  terms: '/terms',
  giftCardLookup: '/gclookup',
  giftCardPurchase: '/gift-cards',
  checkout: '/shop/checkout',
  confirmation: () => {
    const config = getConfig();

    return `${config.publicRuntimeConfig.canonicalUrl}/shop/confirmation`;
  },
  passwordForgot: '/password/forgot',
  passwordReset: '/password/reset',
  userAccountSettings: '/account-settings',
  userAccountFavorites: '/account-favorites',
  intakeForm: '/request-a-call',
  accountListings: '/a/listings',
  accountProfile: '/a/profile',
  accountGiftCardRedeem: '/a/gift-cards/redeem',
  accountListingsNew: '/a/listings/new',
  apiAuthLogin: '/api/auth/login',
  apiAuthSocial: '/api/auth/social',
  apiAuthSso: (token: string) => `/api/auth/sso/?q=${token}`,
  accountBilling: '/a/billing',
  accountPaymentMethodsUpdate: (args?: { redirectUrl?: string }) => {
    if (args && args.redirectUrl) {
      return `/a/payment-methods/update/?redirectUrl=${encodeURIComponent(
        args.redirectUrl
      )}`;
    } else {
      return '/a/payment-methods/update';
    }
  },
  accountListingsDetails: (id: string) => `/a/listings/${id}/details`,
  accountFriendsAndFamilyPageAccess: (id: string) =>
    `/a/listings/${id}/friends-and-family-page-access`,
  onboardingJoin: '/onboarding/join',
  onboardingImportListings: '/onboarding/import-listings',
  onboardingSelectALevel: '/onboarding/select-a-level',
  contactSupport: 'https://finderco.my.site.com/help/s/contactsupport',
  feedback: 'https://finderco.my.site.com/help/s/feedback',
  stripeCustomer(customerId: string) {
    return `https://dashboard.stripe.com/customers/${customerId}`;
  },
  stripeListingSubscription(subscriptionId: string) {
    return `https://dashboard.stripe.com/subscriptions/${subscriptionId}`;
  },
  book: ({
    // slug will be changed to id
    slug,
    checkInDate,
    checkOutDate,
    adultCount,
    childCount,
    infantCount,
    petCount,
  }: {
    slug: string;
    checkInDate?: string;
    checkOutDate?: string;
    adultCount?: number;
    childCount?: number;
    infantCount?: number;
    petCount?: number;
  }) => {
    let queryParams = new URLSearchParams();

    if (adultCount === undefined || adultCount === 0) {
      adultCount = 1;
    }

    if (checkInDate && checkInDate !== undefined) {
      queryParams.set('checkInDate', checkInDate);
    }

    if (checkOutDate && checkOutDate !== undefined) {
      queryParams.set('checkOutDate', checkOutDate);
    }

    queryParams.set('adultCount', adultCount.toString());

    if (childCount !== undefined) {
      queryParams.set('childCount', childCount.toString());
    }

    if (infantCount !== undefined) {
      queryParams.set('infantCount', infantCount.toString());
    }

    if (petCount !== undefined) {
      queryParams.set('petCount', petCount.toString());
    }

    return concatParams(`/book/${slug}`, queryParams);
  },
  trips: '/trips',
  tripDetails: (reservationId: string) => {
    return `/trips/${reservationId}`;
  },
  cancelTrip: (reservationId: string) => `/trips/${reservationId}/cancel`,
  facebook: 'https://www.facebook.com/savvystays',
  x: 'https://x.com/savvy_stays',
  instagram: 'https://www.instagram.com/savvy_stay',
  youtube: 'https://www.youtube.com/@savvystays',
  pinterest: 'https://www.pinterest.com/savvystays/',
  linkedIn: 'https://www.linkedin.com/company/stay-savvy/',
  tiktok: 'https://www.tiktok.com/@savvystays',
  threads: 'https://www.threads.net/@bnbfinder',
  deeplink: (slug: string, redirectUrl: string) =>
    `/deeplink/?slug=${slug}&redirectUrl=${encodeURIComponent(redirectUrl)}`,
} as const;

const regionToPath = ({
  countrySlug,
  levelOneSlug,
  citySlug,
  slug,
}: {
  countrySlug?: string;
  levelOneSlug?: string;
  citySlug?: string;
  slug?: string;
}) => {
  const path = [countrySlug, levelOneSlug, citySlug, slug]
    .filter((value) => value !== null && value !== undefined)
    .join('/');
  return path;
};

const concatParams = (path: string, params: URLSearchParams) => {
  let paramsStr = params.toString();
  if (paramsStr.length > 0) {
    paramsStr = `?${paramsStr}`;
  }
  return `${path}${paramsStr}`;
};

export const canonicalUrlForPath = (path: string) => {
  const url = new URL(path, 'https://www.savvy.com');
  url.search = ''; // Remove query parameters
  return url.toString();
};

export const canonicalUrlForPathWithPage = (path: string, page: number) =>
  page > 1
    ? `${canonicalUrlForPath(path)}?page=${page}`
    : canonicalUrlForPath(path);
